/**
 * Navegação de menu pelo controle (F85): a tela de escolha da classe é a primeira a aceitar controle. O
 * mapeamento é o "standard" do Gamepad API (Xbox, PlayStation e o resto que o navegador normaliza):
 * direcional ou analógico esquerdo movem, A/Cruz confirma, B/Círculo volta.
 *
 * `padActions` é pura (quadro a quadro, com a borda do aperto e a repetição de quem segura);
 * `GamepadNav` só lê `navigator.getGamepads()` a cada quadro e entrega as ações.
 */

export type PadAction = "left" | "right" | "up" | "down" | "confirm" | "back";

/** O que importa de um controle num quadro. */
export interface PadSnapshot {
  readonly buttons: readonly boolean[];
  readonly axes: readonly number[];
}

/** Botões do mapeamento "standard". */
const BUTTON = { confirm: 0, back: 1, up: 12, down: 13, left: 14, right: 15 } as const;
/** Analógico: abaixo disto, parado. */
export const STICK_DEADZONE = 0.5;
/** Segurando uma direção: repete depois de `delay` ms, a cada `every` ms. */
export const PAD_REPEAT = { delay: 400, every: 120 } as const;

const DIRECTIONS = ["left", "right", "up", "down"] as const;

/** O estado entre quadros: o que estava apertado e quando repete. */
export interface PadNavState {
  held: Map<PadAction, number>;
}

export const newPadNavState = (): PadNavState => ({ held: new Map() });

/** Apertado agora (vários controles contam juntos). */
function pressed(pads: readonly PadSnapshot[], action: PadAction): boolean {
  return pads.some((p) => {
    if (p.buttons[BUTTON[action]]) return true;
    const x = p.axes[0] ?? 0;
    const y = p.axes[1] ?? 0;
    if (action === "left") return x <= -STICK_DEADZONE;
    if (action === "right") return x >= STICK_DEADZONE;
    if (action === "up") return y <= -STICK_DEADZONE;
    if (action === "down") return y >= STICK_DEADZONE;
    return false;
  });
}

/**
 * As ações deste quadro: confirmar e voltar só na borda do aperto; as direções na borda e, seguradas,
 * de novo a cada `PAD_REPEAT`. Muda `state`.
 */
export function padActions(state: PadNavState, pads: readonly PadSnapshot[], now: number): PadAction[] {
  const out: PadAction[] = [];
  for (const action of [...DIRECTIONS, "confirm", "back"] as const) {
    const down = pressed(pads, action);
    const next = state.held.get(action);
    if (!down) {
      state.held.delete(action);
      continue;
    }
    if (next === undefined) {
      out.push(action);
      state.held.set(action, (DIRECTIONS as readonly string[]).includes(action) ? now + PAD_REPEAT.delay : Infinity);
    } else if (now >= next) {
      out.push(action);
      state.held.set(action, now + PAD_REPEAT.every);
    }
  }
  return out;
}

/** Lê os controles a cada quadro enquanto ligado e entrega as ações a `onAction`. */
export class GamepadNav {
  private readonly state = newPadNavState();
  private frame = 0;
  private readonly onAction: (action: PadAction) => void;
  private readonly read: () => readonly (Gamepad | null)[];

  constructor(onAction: (action: PadAction) => void, read: () => readonly (Gamepad | null)[] = () => (typeof navigator.getGamepads === "function" ? navigator.getGamepads() : [])) {
    this.onAction = onAction;
    this.read = read;
  }

  start(): void {
    if (this.frame) return;
    // O que já estava apertado ao abrir a tela (o A que confirmou a anterior) não vale.
    padActions(this.state, this.snapshots(), performance.now());
    const tick = (now: number) => {
      this.frame = requestAnimationFrame(tick);
      for (const action of padActions(this.state, this.snapshots(), now)) this.onAction(action);
    };
    this.frame = requestAnimationFrame(tick);
  }

  stop(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private snapshots(): PadSnapshot[] {
    const pads: PadSnapshot[] = [];
    for (const pad of this.read()) if (pad?.connected) pads.push({ buttons: pad.buttons.map((b) => b.pressed), axes: pad.axes });
    return pads;
  }
}
